// Shared realtime client for the shop-floor surfaces — one socket per tab, WS /rt.
//
// The server pushes `{t:"changed", topics:["session:<code>", "sessions"]}` when a write lands
// (src/helpers/realtime/bus.ts). This helper turns that into callbacks: a surface registers
// `rt.on("session:W-…", cb)` and its cb fires when that topic changes — whereupon the surface
// re-runs the SAME fetch its poll loop has always run. Signal, not data: nothing about what a
// screen loads changes, only when.
//
// Why not Component.connectWebSocket: those hooks are per-Component and the framework wrapper
// has no reconnect/backoff/heartbeat (the Hub's liveSocketPane.ts had to build its own on top).
// The four surfaces share ONE socket per tab, which means a plain module-level WebSocket with
// the same proven loop: backoff 1/2/5/10/30 s, a 30 s application-level ping (Apache's tunnel
// idle limit cuts silent sockets — see the integration vhost's story), resubscribe-on-open.
//
// Failure posture: this helper only ever ADDS immediacy. Every subscriber keeps its poll loop
// at a stretched interval (rt.connected() decides the stretch), so a dead socket degrades to
// the pre-realtime behaviour — slower, never stopped. `onReconnect` fires after a DROPPED
// socket comes back so pollers refetch once and cover whatever the gap swallowed.
const BACKOFF_S = [1, 2, 5, 10, 30];
const PING_MS = 30_000;
/** This tab's identity on the bus — echoed on frames so we skip refetching our own writes. */
export const rtOrigin = "t" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const handlers = new Map(); // topic → registrations
const reconnectHandlers = new Set();
let ws = null;
let open = false;
let everOpened = false;
let attempts = 0;
let pingTimer = null;
let retryTimer = null;
let subQueued = false;
function wsUrl() {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${location.host}/rt`;
}
function sendSub() {
    if (!open || !ws)
        return;
    try {
        ws.send(JSON.stringify({ t: "sub", topics: [...handlers.keys()], origin: rtOrigin }));
    }
    catch { /* racing a close — the reconnect path resubscribes */ }
}
/** Coalesce a burst of on()/off() calls into one `sub` frame. */
function queueSub() {
    if (subQueued)
        return;
    subQueued = true;
    setTimeout(() => { subQueued = false; sendSub(); }, 0);
}
function connect() {
    if (ws || retryTimer)
        return;
    if (handlers.size === 0)
        return; // nobody listening → no socket
    let sock;
    try {
        sock = new WebSocket(wsUrl());
    }
    catch {
        scheduleRetry();
        return;
    }
    ws = sock;
    sock.onopen = () => {
        if (ws !== sock)
            return;
        open = true;
        const wasDown = everOpened; // a RE-connect, not the first one
        everOpened = true;
        attempts = 0;
        sendSub();
        if (pingTimer)
            clearInterval(pingTimer);
        pingTimer = setInterval(() => {
            try {
                sock.send(JSON.stringify({ t: "ping" }));
            }
            catch { /* close path handles it */ }
        }, PING_MS);
        if (wasDown)
            for (const fn of [...reconnectHandlers]) {
                try {
                    fn();
                }
                catch { /* subscriber's own problem */ }
            }
    };
    sock.onmessage = (ev) => {
        let m = null;
        try {
            m = JSON.parse(String(ev.data));
        }
        catch {
            return;
        }
        if (m?.t !== "changed" || !Array.isArray(m.topics))
            return;
        // Echo filter is PER SUBSCRIPTION, not per socket: the pane that made the write holds
        // what it wrote (skip), but its neighbours in the same tab — the queue list, the nav
        // badges — show data that write just moved, and they opt in via selfEcho.
        const ownWrite = typeof m.origin === "string" && m.origin === rtOrigin;
        const fired = new Set(); // a frame names 2 topics; fire each cb once
        for (const t of m.topics) {
            for (const reg of handlers.get(String(t)) ?? []) {
                if (ownWrite && !reg.selfEcho)
                    continue;
                if (fired.has(reg.fn))
                    continue;
                fired.add(reg.fn);
                try {
                    reg.fn();
                }
                catch { /* one bad subscriber must not stop the rest */ }
            }
        }
    };
    sock.onclose = () => { if (ws === sock)
        teardown(); };
    sock.onerror = () => { try {
        sock.close();
    }
    catch { /* already closing */ } };
}
function teardown() {
    ws = null;
    open = false;
    if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
    }
    scheduleRetry();
}
function scheduleRetry() {
    if (retryTimer || handlers.size === 0)
        return;
    const delay = BACKOFF_S[Math.min(attempts, BACKOFF_S.length - 1)] * 1000;
    attempts++;
    retryTimer = setTimeout(() => { retryTimer = null; connect(); }, delay);
}
export const rt = {
    /**
     * Fire `cb` whenever `topic` changes. Returns the unsubscribe handle — components MUST call
     * it in onDestroyed, or a dead pane keeps refetching forever.
     * Topics: `session:<code>` (one clienteling session), `sessions` (the list moved),
     * `counter:<n>` (a POS till re-pointed at another order). Each must be allowed by
     * TOPIC_SHAPE in modules/main/controllers/realtime.ts — an unlisted topic is dropped
     * from the subscription silently and this callback simply never fires.
     *
     * `opts.selfEcho`: also fire on THIS tab's own writes. Off by default — the per-session
     * mirrors (POS order state, Assist basket, mini-app pickup) already hold what they just
     * wrote. Aggregate views (queue tabs, pick boards, nav badges) turn it on, because a
     * neighbouring pane's write moves THEIR rows too, and the ~one extra GET lands on the
     * signature guard every list screen already has.
     */
    on(topic, cb, opts) {
        let set = handlers.get(topic);
        if (!set) {
            set = new Set();
            handlers.set(topic, set);
        }
        const reg = { fn: cb, selfEcho: opts?.selfEcho === true };
        set.add(reg);
        queueSub();
        connect();
        return () => {
            const s = handlers.get(topic);
            if (!s)
                return;
            s.delete(reg);
            if (s.size === 0)
                handlers.delete(topic);
            queueSub();
        };
    },
    /**
     * Fire `cb` when a dropped socket comes back — the moment to refetch once, because writes
     * during the outage were never announced. NOT fired on the first connect (nothing missed).
     */
    onReconnect(cb) {
        reconnectHandlers.add(cb);
        return () => reconnectHandlers.delete(cb);
    },
    /**
     * Is the push channel live right now? Pollers use this to pick their interval: connected →
     * the long fallback cadence; not connected → their original polling cadence.
     */
    connected() {
        return open;
    },
};
//# sourceMappingURL=realtime.js.map